import React from 'react';
import styled from 'styled-components';
// Removed PageHero import as hero section is not desired
import { FiFileText, FiUsers, FiCreditCard, FiAlertCircle, FiMail } from 'react-icons/fi';

const TermsContainer = styled.div`
  margin-top: 0;
  overflow-x: hidden;
  width: 100%;
`;

const Container = styled.div`
  max-width: ${props => props.theme.breakpoints.wide};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  width: 100%;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 ${props => props.theme.spacing.md};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    /* Consistent mobile gutters */
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

const Section = styled.section`
  padding: ${props => props.theme.spacing['4xl']} 0;
  
  &:first-of-type {
    padding-top: ${props => props.theme.spacing['2xl']};
  }
`;

// New simple page heading to replace hero
const PageTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['3xl']};
  font-weight: ${props => props.theme.fontWeights.extrabold};
  color: ${props => props.theme.colors.dark};
  margin: 0 0 ${props => props.theme.spacing.md};
`;

const PageSubtitle = styled.p`
  color: ${props => props.theme.colors.gray[700]};
  font-size: ${props => props.theme.fontSizes.lg};
  margin: 0 0 ${props => props.theme.spacing.xl};
  line-height: 1.6;
`;

const ContentBox = styled.div`
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing['3xl']};
  box-shadow: ${props => props.theme.shadows.lg};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing['2xl']};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xl};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.dark};
  margin-bottom: ${props => props.theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};

  svg {
    color: ${props => props.theme.colors.primary};
    font-size: ${props => props.theme.fontSizes.xl};
  }
`;

const Text = styled.p`
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.7;
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSizes.base};

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: ${props => props.theme.colors.dark};
    font-weight: ${props => props.theme.fontWeights.semibold};
  }
`;

const List = styled.ul`
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.7;
  margin: ${props => props.theme.spacing.lg} 0;
  padding-left: ${props => props.theme.spacing.xl};

  li {
    margin-bottom: ${props => props.theme.spacing.sm};
  }

  strong {
    color: ${props => props.theme.colors.dark};
    font-weight: ${props => props.theme.fontWeights.semibold};
  }
`;

const OrderedList = styled.ol`
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.7;
  margin: ${props => props.theme.spacing.lg} 0;
  padding-left: ${props => props.theme.spacing.xl};

  li {
    margin-bottom: ${props => props.theme.spacing.sm};
  }

  strong {
    color: ${props => props.theme.colors.dark};
    font-weight: ${props => props.theme.fontWeights.semibold};
  }
`;

const LastUpdated = styled.div`
  background: ${props => props.theme.colors.gray[100]};
  border-left: 4px solid ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.gray[700]};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const ImportantNote = styled.div`
  background: ${props => props.theme.colors.secondary}20;
  border: 1px solid ${props => props.theme.colors.secondary};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  margin: ${props => props.theme.spacing.lg} 0;

  p {
    margin: 0;
    color: ${props => props.theme.colors.dark};
    font-weight: ${props => props.theme.fontWeights.medium};
  }
`;

const ContactBox = styled.div`
  background: ${props => props.theme.colors.gradient.primary};
  color: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius.xl};
  text-align: center;

  h3 {
    color: ${props => props.theme.colors.light};
    margin-bottom: ${props => props.theme.spacing.md};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${props => props.theme.spacing.sm};

    svg {
      font-size: ${props => props.theme.fontSizes.lg};
    }
  }

  p {
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: ${props => props.theme.spacing.lg};
  }

  a {
    color: ${props => props.theme.colors.light};
    font-weight: ${props => props.theme.fontWeights.semibold};
    text-decoration: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    transition: ${props => props.theme.transitions.default};

    &:hover {
      border-bottom-color: ${props => props.theme.colors.light};
    }
  }
`;

const TermsOfService = () => {
  return (
    <TermsContainer>
      {/* Removed PageHero, using a simple heading */}
      <Section>
        <Container>
          <PageTitle>Terms of Service</PageTitle>
          <PageSubtitle>
            These terms and conditions govern your use of our educational services and website. Please read them carefully before using our platform.
          </PageSubtitle>

          <LastUpdated>
            <strong>Last Updated:</strong> December 2024
          </LastUpdated>

          <ContentBox>
            <SectionTitle>
              <FiFileText />
              Acceptance of Terms
            </SectionTitle>
            <Text>
              By accessing and using the Service Hub IT Solutions website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree with these terms, please do not use our services.
            </Text>
            <ImportantNote>
              <p>
                These terms constitute a legally binding agreement between you and Service Hub IT Solutions.
              </p>
            </ImportantNote>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              Description of Services
            </SectionTitle>
            <Text>
              Service Hub IT Solutions provides comprehensive technology education and training services, including:
            </Text>
            <List>
              <li><strong>Online and Offline Courses:</strong> Web development, mobile app development, data science, digital marketing, and other technology-related subjects.</li>
              <li><strong>Training Programs:</strong> Professional certification programs and skill development workshops.</li>
              <li><strong>Educational Resources:</strong> Learning materials, tutorials, and project assignments.</li>
              <li><strong>Career Services:</strong> Job placement assistance and career guidance.</li>
              <li><strong>Community Platform:</strong> Access to our online learning community and forums.</li>
            </List>
            <Text>
              We reserve the right to modify, suspend, or discontinue any aspect of our services at any time with or without notice.
            </Text>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              <FiUsers />
              User Accounts and Responsibilities
            </SectionTitle>
            <Text>
              To access certain features of our services, you may need to create an account. You are responsible for:
            </Text>
            <List>
              <li><strong>Account Security:</strong> Maintaining the confidentiality of your login credentials and account information.</li>
              <li><strong>Accurate Information:</strong> Providing truthful and accurate information during registration and keeping it up to date.</li>
              <li><strong>Authorized Use:</strong> Ensuring that only you use your account and not sharing access with others.</li>
              <li><strong>Compliance:</strong> Following all applicable laws and these terms while using our services.</li>
              <li><strong>Content:</strong> Taking responsibility for any content you submit or share through our platform.</li>
            </List>
            <Text>
              You must be at least 16 years old to create an account. If you are under 18, you must have parental or guardian consent.
            </Text>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              <FiCreditCard />
              Course Enrollment and Payments
            </SectionTitle>
            <Text>
              Our course enrollment and payment policies include:
            </Text>
            <OrderedList>
              <li><strong>Course Fees:</strong> All course fees are clearly stated on our website and must be paid in full before course commencement unless otherwise specified.</li>
              <li><strong>Payment Methods:</strong> We accept various payment methods including credit cards, debit cards, and online banking transfers.</li>
              <li><strong>Refund Policy:</strong> Refunds are available within 7 days of course enrollment, provided that less than 25% of the course content has been accessed.</li>
              <li><strong>Installment Plans:</strong> Some courses offer installment payment options, subject to additional terms and conditions.</li>
              <li><strong>Price Changes:</strong> We reserve the right to change course prices at any time, but price changes will not affect enrollments already completed.</li>
            </OrderedList>
            <ImportantNote>
              <p>
                Refund requests must be submitted in writing and are subject to review. Processing may take 5-10 business days.
              </p>
            </ImportantNote>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              Intellectual Property Rights
            </SectionTitle>
            <Text>
              All content provided through our services, including but not limited to course materials, videos, text, graphics, logos, and software, is the property of Service Hub IT Solutions or our licensors and is protected by intellectual property laws.
            </Text>
            <Text>
              <strong>Your Rights:</strong> You are granted a limited, non-exclusive, non-transferable license to access and use our content solely for your personal educational purposes.
            </Text>
            <Text>
              <strong>Restrictions:</strong> You may not reproduce, distribute, modify, create derivative works from, publicly display, or commercially exploit our content without written permission.
            </Text>
            <Text>
              <strong>User Content:</strong> By submitting content to our platform, you grant us a non-exclusive, royalty-free license to use, modify, and display such content for educational and promotional purposes.
            </Text>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              Code of Conduct
            </SectionTitle>
            <Text>
              To maintain a positive learning environment, all users must adhere to our code of conduct:
            </Text>
            <List>
              <li><strong>Respectful Behavior:</strong> Treat instructors, staff, and fellow students with respect and courtesy.</li>
              <li><strong>Academic Integrity:</strong> Complete assignments and projects honestly without plagiarism or cheating.</li>
              <li><strong>Appropriate Communication:</strong> Use professional and appropriate language in all interactions.</li>
              <li><strong>No Harassment:</strong> Harassment, discrimination, or bullying of any kind is strictly prohibited.</li>
              <li><strong>Legal Compliance:</strong> Do not engage in any illegal activities or share illegal content.</li>
              <li><strong>Platform Security:</strong> Do not attempt to hack, disrupt, or compromise our systems or other users' accounts.</li>
            </List>
            <Text>
              Violation of these guidelines may result in suspension or termination of your access to our services.
            </Text>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              <FiAlertCircle />
              Limitation of Liability
            </SectionTitle>
            <Text>
              To the maximum extent permitted by law, Service Hub IT Solutions and its affiliates, directors, employees, and agents shall not be liable for:
            </Text>
            <List>
              <li>Any indirect, incidental, consequential, or punitive damages</li>
              <li>Loss of profits, data, or business opportunities</li>
              <li>Service interruptions or technical difficulties</li>
              <li>Actions or omissions of third-party service providers</li>
              <li>Job placement outcomes or career advancement results</li>
            </List>
            <Text>
              Our total liability for any claims arising from your use of our services shall not exceed the amount you have paid to us for the specific service giving rise to the claim.
            </Text>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              Termination
            </SectionTitle>
            <Text>
              Either party may terminate this agreement under the following circumstances:
            </Text>
            <Text>
              <strong>By You:</strong> You may stop using our services and close your account at any time by contacting our support team.
            </Text>
            <Text>
              <strong>By Us:</strong> We may suspend or terminate your access if you violate these terms, fail to pay required fees, or engage in activities that harm our platform or other users.
            </Text>
            <Text>
              Upon termination, your access to course materials will cease, but you may retain certificates for completed courses. Some provisions of these terms will survive termination, including intellectual property rights and limitation of liability clauses.
            </Text>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              Changes to Terms
            </SectionTitle>
            <Text>
              We reserve the right to modify these Terms of Service at any time. When we make material changes, we will:
            </Text>
            <OrderedList>
              <li>Post the updated terms on our website</li>
              <li>Send email notifications to registered users</li>
              <li>Display a notice on our platform for at least 30 days</li>
            </OrderedList>
            <Text>
              Your continued use of our services after the changes take effect constitutes acceptance of the updated terms. If you disagree with the changes, you should discontinue using our services.
            </Text>
          </ContentBox>

          <ContentBox>
            <SectionTitle>
              Governing Law and Disputes
            </SectionTitle>
            <Text>
              These Terms of Service are governed by the laws of India. Any disputes arising from these terms or your use of our services will be resolved through:
            </Text>
            <OrderedList>
              <li><strong>Direct Communication:</strong> We encourage users to contact us directly to resolve issues informally.</li>
              <li><strong>Mediation:</strong> If direct communication fails, disputes may be resolved through mediation.</li>
              <li><strong>Arbitration:</strong> Binding arbitration may be required for certain types of disputes.</li>
              <li><strong>Jurisdiction:</strong> Courts in Hyderabad, India shall have exclusive jurisdiction over any legal proceedings.</li>
            </OrderedList>
          </ContentBox>

          <ContactBox>
            <h3>
              <FiMail />
              Questions About These Terms?
            </h3>
            <p>
              If you have any questions about these Terms of Service or need clarification on any provisions, please contact us.
            </p>
            <p>
              Email: <a href="mailto:legal@servicehubitsolutions.com">legal@servicehubitsolutions.com</a><br />
              Phone: <a href="tel:+916301313334">+91 6301313334</a><br />
              Address: Service Hub IT Solutions, Hyderabad, India
            </p>
          </ContactBox>
        </Container>
      </Section>
    </TermsContainer>
  );
};

export default TermsOfService;
